import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, AlertTriangle, Globe, Wifi, Car, Phone, Radio, Server,
  BrainCircuit, BarChart3, Lock, Eye, Target, Zap, Activity,
  ArrowRight, ArrowLeft, Play, Pause, DollarSign, Users,
  CheckCircle, TrendingUp, Cpu, Layers, Award, Fingerprint
} from 'lucide-react';
import SEO from '../components/SEO';

const slides = [
  // SLIDE 1: Title
  {
    id: 'title',
    layout: 'center',
    content: {
      badge: 'Confidential — Partner & Enterprise Sales Deck',
      title: 'SecureSphere',
      subtitle: 'by Infuse.AI',
      tagline: 'The Only Unified Security Platform for Mobile, IoT, Automotive, Telecom & AI Workloads',
      footer: 'infuse-ai.in | info@infuse.net.in'
    }
  },
  // SLIDE 2: WHY — The Problem
  {
    id: 'why-problem',
    layout: 'split',
    content: {
      badge: 'WHY',
      title: 'The Connected World Is Under Attack',
      left: {
        heading: 'The Threat Landscape',
        stats: [
          { value: '10.5B+', label: 'Connected IoT devices globally', icon: Wifi },
          { value: '$10.5T', label: 'Estimated cybercrime cost by 2026', icon: DollarSign },
          { value: '43%', label: 'Of attacks target SMBs and carriers', icon: Target },
          { value: '277 days', label: 'Average time to detect a breach', icon: Activity }
        ]
      },
      right: {
        heading: 'The Gaps We Fill',
        items: [
          'No single platform covers mobile + IoT + automotive + telecom + AI',
          'Existing vendors (PaloAlto, Aeris WatchTower) miss AI workload security entirely',
          'Carriers need carrier-grade scale — not enterprise-lite solutions',
          'AI/ML adoption is exploding — but AI security governance is nonexistent',
          'Prompt injection attacks up 300% — most enterprises have zero protection'
        ]
      }
    }
  },
  // SLIDE 3: WHAT — Our Solution
  {
    id: 'what-solution',
    layout: 'modules',
    content: {
      badge: 'WHAT',
      title: 'SecureSphere: Unified Enterprise Security',
      subtitle: '22 security modules under one intelligent platform',
      modules: [
        { icon: Phone, name: 'Mobile Protection', desc: 'MDM, URL scanning, SMS phishing' },
        { icon: Wifi, name: 'IoT Security', desc: 'Device registry, firmware scanning' },
        { icon: Car, name: 'Automotive', desc: 'CAN bus, ECU, V2X, GPS protection' },
        { icon: Radio, name: 'GSM Fraud', desc: 'SIM swap, IMSI catcher, OTP fraud' },
        { icon: Server, name: 'Telecom/CSP', desc: 'SS7, Diameter, GTP, eSIM, 5G' },
        { icon: Eye, name: 'Surveillance IoT', desc: 'Cameras, sensors, smart meters' },
        { icon: BrainCircuit, name: 'AI Security', desc: 'Prompt injection, guardrails, SBOM' },
        { icon: BarChart3, name: 'Analytics', desc: 'Threat trends, compliance, executive' },
        { icon: Zap, name: 'Real-Time Alerts', desc: 'WebSocket, multi-channel delivery' },
        { icon: Shield, name: 'Intelligence', desc: 'Zero-day, cross-protocol, hunting' }
      ]
    }
  },
  // SLIDE 4: USP — What Makes Us Different
  {
    id: 'usp',
    layout: 'comparison',
    content: {
      badge: 'USP',
      title: 'Why SecureSphere Wins',
      columns: [
        {
          heading: 'SecureSphere',
          highlight: true,
          items: [
            { text: 'Unified mobile + IoT + auto + telecom + AI', check: true },
            { text: 'AI Security Platform (prompt injection, SBOM)', check: true },
            { text: 'Carrier-grade: 10B+ events/day', check: true },
            { text: 'Real-time WebSocket alerts (<8ms)', check: true },
            { text: 'EU AI Act + NIST AI RMF compliance', check: true },
            { text: 'Agentic AI guardrails & governance', check: true },
            { text: 'SS7/Diameter/GTP protocol security', check: true }
          ]
        },
        {
          heading: 'PaloAlto',
          highlight: false,
          items: [
            { text: 'Network + endpoint only', check: false },
            { text: 'No AI workload security', check: false },
            { text: 'Enterprise-focused, not carrier-grade', check: false },
            { text: 'Batch alerts only', check: false },
            { text: 'Limited AI compliance tooling', check: false },
            { text: 'No agentic AI capabilities', check: false },
            { text: 'No telecom protocol support', check: false }
          ]
        },
        {
          heading: 'Aeris WatchTower',
          highlight: false,
          items: [
            { text: 'IoT + cellular only', check: false },
            { text: 'No AI security module', check: false },
            { text: 'IoT-focused, limited breadth', check: false },
            { text: 'Standard alerting', check: false },
            { text: 'Basic compliance only', check: false },
            { text: 'No AI agent monitoring', check: false },
            { text: 'Limited signaling security', check: false }
          ]
        }
      ]
    }
  },
  // SLIDE 5: TECHNOLOGY
  {
    id: 'technology',
    layout: 'tech',
    content: {
      badge: 'TECHNOLOGY',
      title: 'SecureSphere AI Shield v2.0',
      capabilities: [
        { icon: BrainCircuit, title: 'AI-Native Detection', desc: 'ML models trained on 10B+ events. 99.8% detection accuracy. Zero-day discovery via behavioral anomaly detection.' },
        { icon: Fingerprint, title: 'Prompt Injection Shield', desc: '6 injection vectors detected: direct, indirect, role-play, token smuggling, context overflow, multi-turn manipulation.' },
        { icon: Layers, title: 'Multi-Protocol Analysis', desc: 'Simultaneous monitoring of SS7, Diameter, GTP, HTTP, MQTT, CAN bus, V2X, and custom protocols.' },
        { icon: Zap, title: 'Real-Time Engine', desc: 'WebSocket push alerts. 8ms average response time. Process 500K events/second per node.' },
        { icon: Lock, title: 'Zero-Trust Architecture', desc: 'JWT + RBAC, per-request authentication, department-level AI usage policies, immutable 365-day audit trail.' },
        { icon: Cpu, title: 'Deployment Flexibility', desc: 'Cloud SaaS, on-premises, or hybrid deployment. Kubernetes-native. SOC 2 ready infrastructure.' }
      ]
    }
  },
  // SLIDE 6: HOW — For Enterprise
  {
    id: 'how-enterprise',
    layout: 'split',
    content: {
      badge: 'HOW — Enterprise',
      title: 'Enterprise Deployment',
      left: {
        heading: 'Integration in Days, Not Months',
        stats: [
          { value: '< 1 week', label: 'Time to first value', icon: Zap },
          { value: 'API + SDK', label: 'Integration methods', icon: Cpu },
          { value: 'SIEM-ready', label: 'Splunk, QRadar, Sentinel', icon: Server },
          { value: 'SSO/SCIM', label: 'Enterprise identity integration', icon: Users }
        ]
      },
      right: {
        heading: 'What Enterprises Get',
        items: [
          'Unified security dashboard across ALL device types and AI workloads',
          'AI compliance governance (EU AI Act, NIST AI RMF, ISO 42001)',
          'Department-level cost tracking and usage policies',
          'Role-based access control (Admin, Analyst, Viewer)',
          'Executive-ready compliance reports and audit trails'
        ]
      }
    }
  },
  // SLIDE 7: HOW — For Carriers
  {
    id: 'how-carriers',
    layout: 'split',
    content: {
      badge: 'HOW — Carriers',
      title: 'Carrier-Grade Deployment',
      left: {
        heading: 'Built for Carrier Scale',
        stats: [
          { value: '120M+', label: 'Subscribers protected', icon: Users },
          { value: '10B+', label: 'Events processed daily', icon: Activity },
          { value: '99.97%', label: 'Platform uptime SLA', icon: Shield },
          { value: '3 continents', label: 'Global deployments', icon: Globe }
        ]
      },
      right: {
        heading: 'Carrier Value Proposition',
        items: [
          'SS7, Diameter, GTP signaling protection — block attacks in real-time',
          'SIM swap detection saving $4.2M+ per quarter per carrier',
          'White-label capability for subscriber-facing security products',
          'eSIM lifecycle management and network slice security for 5G',
          'Revenue-generating: offer SecureSphere as a value-added service'
        ]
      }
    }
  },
  // SLIDE 8: For Partners
  {
    id: 'partners',
    layout: 'split',
    content: {
      badge: 'PARTNER PROGRAM',
      title: 'Channel Partner Opportunity',
      left: {
        heading: 'Partner Economics',
        stats: [
          { value: '30-40%', label: 'Partner margin on deals', icon: DollarSign },
          { value: '$500K+', label: 'Average deal size (carrier)', icon: TrendingUp },
          { value: 'White-label', label: 'Offer under your brand', icon: Award },
          { value: 'MDF + leads', label: 'Marketing support', icon: Users }
        ]
      },
      right: {
        heading: 'Why Partner With Us',
        items: [
          'Only vendor covering the full mobile → IoT → automotive → telecom → AI stack',
          'Massive TAM: $248B cybersecurity market growing 12% CAGR',
          'Recurring revenue: 95%+ customer renewal rate',
          'Technical enablement: dedicated partner SE support',
          'Deal registration protection and co-sell with our enterprise team'
        ]
      }
    }
  },
  // SLIDE 9: ROI & Pricing
  {
    id: 'pricing',
    layout: 'pricing',
    content: {
      badge: 'PRICING',
      title: 'Plans Built for Every Scale',
      plans: [
        { name: 'Starter', price: '$2,499/mo', target: 'Growing businesses', features: ['Up to 5,000 devices', '5 security modules', 'Email alerts', 'Standard support', 'Monthly reports'] },
        { name: 'Enterprise', price: '$7,999/mo', target: 'Large enterprises', highlight: true, features: ['Up to 50,000 devices', 'All 22 security modules', 'AI Security Platform', 'Multi-channel alerts', '24/7 premium support', 'RBAC + SSO', 'Custom compliance'] },
        { name: 'Carrier', price: 'Custom', target: 'Telecom operators', features: ['Unlimited subscribers', 'SS7/Diameter/GTP protection', 'White-label option', 'On-premises deployment', 'Dedicated support team', 'Custom SLA (99.99%)'] }
      ],
      roi: [
        { metric: '$4.2M', label: 'Average savings per quarter from GSM fraud prevention' },
        { metric: '6 months', label: 'Faster ISO 21434 compliance for automotive customers' },
        { metric: '80%', label: 'Reduction in mean time to detect (MTTD)' }
      ]
    }
  },
  // SLIDE 10: CTA
  {
    id: 'cta',
    layout: 'center',
    content: {
      badge: 'NEXT STEPS',
      title: 'Let\'s Secure Your Enterprise',
      subtitle: '',
      tagline: 'Schedule a personalized demo with our security architects',
      items: [
        { icon: CheckCircle, text: 'Custom POC within 2 weeks' },
        { icon: CheckCircle, text: 'Free security assessment of your current stack' },
        { icon: CheckCircle, text: 'No-commitment 30-day pilot program' }
      ],
      footer: 'info@infuse.net.in | +91-9599960663 | infuse-ai.in'
    }
  }
];

// Slide renderers
const CenterSlide = ({ content }) => (
  <div className="flex flex-col items-center justify-center text-center h-full min-h-[60vh]">
    <span className="inline-block px-4 py-1.5 bg-orange-500/10 border border-orange-500/30 rounded-full text-[#F7B731] text-sm font-medium mb-8">{content.badge}</span>
    <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black mb-2 text-white">{content.title}</h1>
    {content.subtitle && <p className="text-2xl text-orange-400 font-semibold mb-6">{content.subtitle}</p>}
    <p className="text-lg text-neutral-300 max-w-3xl mb-8">{content.tagline}</p>
    {content.items && (
      <div className="flex flex-col gap-3 mb-8">
        {content.items.map((item) => (
          <div key={item.text} className="flex items-center gap-2 text-neutral-300"><item.icon className="w-5 h-5 text-green-400" />{item.text}</div>
        ))}
      </div>
    )}
    <p className="text-sm text-neutral-500">{content.footer}</p>
  </div>
);

const SplitSlide = ({ content }) => (
  <div className="h-full">
    <span className="inline-block px-3 py-1 bg-orange-500/10 border border-orange-500/30 rounded-full text-[#F7B731] text-xs font-medium mb-4">{content.badge}</span>
    <h2 className="text-3xl sm:text-4xl font-bold text-white mb-8">{content.title}</h2>
    <div className="grid md:grid-cols-2 gap-8">
      <div>
        <h3 className="text-lg font-semibold text-orange-400 mb-4">{content.left.heading}</h3>
        <div className="space-y-4">
          {content.left.stats.map((s) => (
            <motion.div key={s.label} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.1 }} className="flex items-center gap-4 bg-[#242424] rounded-xl p-4 border border-white/10">
              <s.icon className="w-6 h-6 text-orange-400 flex-shrink-0" />
              <div><p className="text-xl font-bold text-white">{s.value}</p><p className="text-xs text-neutral-400">{s.label}</p></div>
            </motion.div>
          ))}
        </div>
      </div>
      <div>
        <h3 className="text-lg font-semibold text-orange-400 mb-4">{content.right.heading}</h3>
        <div className="space-y-3">
          {content.right.items.map((item) => (
            <motion.div key={item} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 + i * 0.1 }} className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-1" />
              <span className="text-sm text-neutral-300">{item}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const ModulesSlide = ({ content }) => (
  <div>
    <span className="inline-block px-3 py-1 bg-orange-500/10 border border-orange-500/30 rounded-full text-[#F7B731] text-xs font-medium mb-4">{content.badge}</span>
    <h2 className="text-3xl font-bold text-white mb-2">{content.title}</h2>
    <p className="text-neutral-400 mb-8">{content.subtitle}</p>
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
      {content.modules.map((mod) => (
        <motion.div key={mod.name} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1 + i * 0.05 }} className="bg-[#242424] border border-white/10 rounded-xl p-4 hover:border-orange-500/30 transition text-center">
          <mod.icon className="w-7 h-7 text-orange-400 mx-auto mb-2" />
          <p className="text-white font-semibold text-sm mb-1">{mod.name}</p>
          <p className="text-xs text-neutral-500">{mod.desc}</p>
        </motion.div>
      ))}
    </div>
  </div>
);

const ComparisonSlide = ({ content }) => (
  <div>
    <span className="inline-block px-3 py-1 bg-orange-500/10 border border-orange-500/30 rounded-full text-[#F7B731] text-xs font-medium mb-4">{content.badge}</span>
    <h2 className="text-3xl font-bold text-white mb-8">{content.title}</h2>
    <div className="grid md:grid-cols-3 gap-4">
      {content.columns.map((col) => (
        <motion.div key={col.heading} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className={`rounded-2xl p-5 border ${col.highlight ? 'bg-gradient-to-b from-orange-500/10 to-[#242424] border-orange-500/40' : 'bg-[#242424] border-white/10'}`}>
          <h3 className={`font-bold text-lg mb-4 ${col.highlight ? 'text-orange-400' : 'text-neutral-400'}`}>{col.heading}</h3>
          <div className="space-y-2.5">
            {col.items.map((item) => (
              <div key={item.text} className="flex items-start gap-2">
                {item.check ? <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" /> : <AlertTriangle className="w-4 h-4 text-red-400/50 flex-shrink-0 mt-0.5" />}
                <span className={`text-xs ${item.check ? 'text-neutral-300' : 'text-neutral-500'}`}>{item.text}</span>
              </div>
            ))}
          </div>
        </motion.div>
      ))}
    </div>
  </div>
);

const TechSlide = ({ content }) => (
  <div>
    <span className="inline-block px-3 py-1 bg-orange-500/10 border border-orange-500/30 rounded-full text-[#F7B731] text-xs font-medium mb-4">{content.badge}</span>
    <h2 className="text-3xl font-bold text-white mb-8">{content.title}</h2>
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {content.capabilities.map((cap) => (
        <motion.div key={cap.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-[#242424] border border-white/10 rounded-xl p-5 hover:border-orange-500/30 transition">
          <cap.icon className="w-7 h-7 text-orange-400 mb-3" />
          <h3 className="text-white font-semibold mb-1">{cap.title}</h3>
          <p className="text-xs text-neutral-400 leading-relaxed">{cap.desc}</p>
        </motion.div>
      ))}
    </div>
  </div>
);

const PricingSlide = ({ content }) => (
  <div>
    <span className="inline-block px-3 py-1 bg-orange-500/10 border border-orange-500/30 rounded-full text-[#F7B731] text-xs font-medium mb-4">{content.badge}</span>
    <h2 className="text-3xl font-bold text-white mb-6">{content.title}</h2>
    <div className="grid md:grid-cols-3 gap-4 mb-8">
      {content.plans.map((plan) => (
        <motion.div key={col.heading} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className={`rounded-2xl p-5 border ${plan.highlight ? 'bg-gradient-to-b from-orange-500/10 to-[#242424] border-orange-500/40' : 'bg-[#242424] border-white/10'}`}>
          <h3 className="text-white font-bold text-lg">{plan.name}</h3>
          <p className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#F7B731] to-[#F26419] mb-1">{plan.price}</p>
          <p className="text-xs text-neutral-500 mb-4">{plan.target}</p>
          <div className="space-y-2">
            {plan.features.map((f) => (
              <div key={f} className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-green-400" /><span className="text-xs text-neutral-300">{f}</span></div>
            ))}
          </div>
        </motion.div>
      ))}
    </div>
    <div className="grid md:grid-cols-3 gap-4">
      {content.roi.map((r) => (
        <div key={r.metric} className="bg-[#1a1a1a] border border-green-500/20 rounded-xl p-4 text-center">
          <p className="text-2xl font-black text-green-400">{r.metric}</p>
          <p className="text-xs text-neutral-400">{r.label}</p>
        </div>
      ))}
    </div>
  </div>
);

const RENDERERS = { center: CenterSlide, split: SplitSlide, modules: ModulesSlide, comparison: ComparisonSlide, tech: TechSlide, pricing: PricingSlide };

const PresentationPage = () => {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(false);
  const timerRef = useRef(null);

  const advance = useCallback(() => setCurrent(p => (p + 1) % slides.length), []);

  useEffect(() => {
    if (playing) { timerRef.current = setInterval(advance, 12000); }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [playing, advance]);

  const goTo = (i) => { setCurrent(i); setPlaying(false); if (timerRef.current) clearInterval(timerRef.current); };

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); goTo((current + 1) % slides.length); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); goTo((current - 1 + slides.length) % slides.length); }
      if (e.key === 'Escape') navigate('/');
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  const slide = slides[current];
  const Renderer = RENDERERS[slide.layout] || CenterSlide;

  return (
    <div className="min-h-screen bg-[#1a1a1a] text-white flex flex-col">
      <SEO title={`SecureSphere Sales Deck — ${slide.content.title || slide.content.badge}`} description="SecureSphere product presentation for partners, enterprises, and telecom carriers." />

      {/* Top bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-[#1a1a1a]/95 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 hover:opacity-80 transition">
            <img src="/infuse-logo.jpg" alt="Infuse.AI" className="h-8 w-8 rounded-lg" />
            <span className="font-bold text-sm"><span className="text-white">Infuse</span><span className="text-[#F26419]">.AI</span></span>
          </button>
          <div className="flex items-center gap-4">
            <span className="text-sm text-neutral-500">{current + 1} / {slides.length}</span>
            <button onClick={() => setPlaying(!playing)} className="px-3 py-1.5 bg-orange-500/20 text-orange-400 rounded-full text-xs flex items-center gap-1 hover:bg-orange-500/30 transition">
              {playing ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />} {playing ? 'Pause' : 'Auto'}
            </button>
          </div>
        </div>
        {/* Progress */}
        <div className="h-0.5 bg-black/30"><motion.div className="h-full bg-gradient-to-r from-[#F7B731] to-[#F26419]" animate={{ width: `${((current + 1) / slides.length) * 100}%` }} transition={{ duration: 0.3 }} /></div>
      </div>

      {/* Slide Content */}
      <div className="flex-1 pt-20 pb-20 px-6 max-w-7xl mx-auto w-full">
        <AnimatePresence mode="wait">
          <motion.div key={slide.id} initial={{ opacity: 0, x: 60 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -60 }} transition={{ duration: 0.35 }}>
            <Renderer content={slide.content} />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Bottom controls */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#1a1a1a]/95 backdrop-blur-xl border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <button onClick={() => goTo((current - 1 + slides.length) % slides.length)} className="flex items-center gap-1 text-neutral-400 hover:text-white transition text-sm"><ArrowLeft className="w-4 h-4" /> Prev</button>
          <div className="flex gap-1.5">
            {slides.map((s, i) => (
              <button key={s.id} onClick={() => goTo(i)} className={`w-2 h-2 rounded-full transition-all ${i === current ? 'bg-orange-400 w-6' : 'bg-neutral-700 hover:bg-neutral-500'}`} />
            ))}
          </div>
          <button onClick={() => goTo((current + 1) % slides.length)} className="flex items-center gap-1 text-neutral-400 hover:text-white transition text-sm">Next <ArrowRight className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
};

export default PresentationPage;
